// Per-entity nouns for the shared lifecycle modals. The modal structure,
// layout, and confirmation mechanics are identical to the Song Library; only
// the nouns and the entity-appropriate consequence sentence differ.

import type {
  CatalogEntityCopy,
  CatalogEntityKind,
} from "./catalogLifecycleTypes";

export const CATALOG_ENTITY_COPY: Record<CatalogEntityKind, CatalogEntityCopy> =
  {
    artist: {
      kind: "artist",
      noun: "artist",
      nounPlural: "artists",
      Noun: "Artist",
      listName: "Artists",
      refKind: "artist",
      archivedApiStatus: "deleted",
    },
    album: {
      kind: "album",
      noun: "album",
      nounPlural: "albums",
      Noun: "Album",
      listName: "Albums",
      refKind: "album",
      archivedApiStatus: "archived",
    },
    "record-label": {
      kind: "record-label",
      noun: "record label",
      nounPlural: "record labels",
      Noun: "Record Label",
      listName: "Record Labels",
      refKind: "recordLabel",
      archivedApiStatus: "deleted",
    },
    genre: {
      kind: "genre",
      noun: "genre",
      nounPlural: "genres",
      Noun: "Genre",
      listName: "Genres",
      refKind: "genre",
      archivedApiStatus: "deleted",
    },
    subgenre: {
      kind: "subgenre",
      noun: "subgenre",
      nounPlural: "subgenres",
      Noun: "Subgenre",
      listName: "Subgenres",
      refKind: "subgenre",
      archivedApiStatus: "deleted",
    },
  };

export function catalogCopy(kind: CatalogEntityKind): CatalogEntityCopy {
  return CATALOG_ENTITY_COPY[kind];
}

/**
 * Consequence sentence in the Archive confirmation. The Song Library says the
 * song "becomes not playable"; for a reference entity the meaningful
 * consequence is that it leaves Active while its songs and audit history stay
 * intact until permanent delete.
 */
export function archiveConsequence(kind: CatalogEntityKind): string {
  const { noun, listName } = catalogCopy(kind);
  return `The ${noun} moves to Archived ${listName} and is removed from Active. Songs and audit history are preserved until the ${noun} is permanently deleted.`;
}

export function restoreConsequence(kind: CatalogEntityKind): string {
  const { noun, listName } = catalogCopy(kind);
  return `The ${noun} returns to Active ${listName}. Archive history is retained — restore never erases when or why it was archived.`;
}

export function batchArchiveConsequence(kind: CatalogEntityKind): string {
  const { noun } = catalogCopy(kind);
  return `Each ${noun} moves to Archived and is removed from Active. Archiving is blocked for any ${noun} still referenced by a living song.`;
}

export function batchRestoreConsequence(kind: CatalogEntityKind): string {
  const { noun, Noun } = catalogCopy(kind);
  return `Each ${noun} returns to Active. ${Noun} ID and archive history are preserved.`;
}
